import { useCallback, useEffect, useRef, useState, isValidElement } from "react"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"

const THUMB_SIZE = 12

export const InspectorPanelAccordionSlider = ({
    title,
    resetValue,
    existingSliderValue,
    minSliderValue,
    maxSliderValue,
    onValueChanged,
    createTextualValue,
}: {
    title?: string | React.ReactNode
    resetValue?: number
    existingSliderValue: number
    minSliderValue: number
    maxSliderValue: number
    onValueChanged: (value: number) => void

    /**
     * Optional function to transform the slider value
     * into some displayable string.
     * @default (value) => `${value / max}%`
     */
    createTextualValue?: (value: number) => string
}) => {
    const defaultCreateTextualValue = (value: number) =>
        `${Math.round(((value - minSliderValue) / (maxSliderValue - minSliderValue)) * 100)}%`

    const [isHovering, setIsHovering] = useState(false)
    const [isHoveringThumb, setIsHoveringThumb] = useState(false)
    const [isActive, setIsActive] = useState(false)

    const [sliderValue, setSliderValue] = useState(existingSliderValue)
    const [textualValue, setTextualValue] = useState(
        !!createTextualValue
            ? createTextualValue(existingSliderValue)
            : defaultCreateTextualValue(existingSliderValue)
    )

    useEffect(() => {
        if (isActive) {
            inputRef.current?.style.setProperty("--thumb-color", "#27bbff") // blue-500
            inputRef.current?.style.setProperty("--thumb-border", "transparent")
        } else if (isHoveringThumb) {
            inputRef.current?.style.setProperty("--thumb-color", "#27bbff") // blue-500
            inputRef.current?.style.setProperty("--thumb-border", "#ffffff") // gray-0
        } else {
            inputRef.current?.style.setProperty("--thumb-color", null)
            inputRef.current?.style.setProperty("--thumb-border", null)
        }
    }, [isHoveringThumb, isActive])

    useEffect(() => {
        if (!isHovering) setIsHoveringThumb(false)
    }, [isHovering])

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLInputElement>) => {
            if (!isHovering || !inputRef.current) return
            const rect = inputRef.current?.getBoundingClientRect()
            const min = Number(inputRef.current.min)
            const max = Number(inputRef.current.max)
            const value = Number(inputRef.current.value)
            const percent = (value - min) / (max - min)
            const thumbLeft = rect.left + percent * (rect.width - THUMB_SIZE)
            const thumbRight = thumbLeft + THUMB_SIZE
            setIsHoveringThumb(e.clientX >= thumbLeft && e.clientX <= thumbRight)
        },
        [isHovering]
    )

    // Update the local state when the selected flight changes.
    useEffectUnsafe(() => {
        setSliderValue(existingSliderValue)
        setTextualValue(
            !!createTextualValue
                ? createTextualValue(existingSliderValue)
                : defaultCreateTextualValue(existingSliderValue)
        )
    }, [existingSliderValue, maxSliderValue, minSliderValue, createTextualValue])

    const inputRef = useRef<HTMLInputElement>(null)

    const onValueChangedRef = useRef(onValueChanged)
    useEffectUnsafe(() => {
        onValueChangedRef.current = onValueChanged
    }, [onValueChanged])

    // Only notify for the initial value and for edits made here — notifying for values synced in
    // from above makes the two directions overwrite each other on every commit.
    const shouldNotifyRef = useRef(true)

    useEffectUnsafe(() => {
        if (shouldNotifyRef.current) {
            shouldNotifyRef.current = false
            onValueChangedRef.current(sliderValue)
        }
        inputRef.current?.style.setProperty("--progress", defaultCreateTextualValue(sliderValue))
    }, [sliderValue, minSliderValue, maxSliderValue])

    return (
        <div className="flex flex-col gap-2 w-full select-none">
            {/* Title & Reset Button */}
            <div className="flex gap-1 items-center h-5">
                {/* Title */}
                {title &&
                    (isValidElement(title) ? title : <h5 className="text-gray-0">{title}</h5>)}

                {/* Spacer */}
                <div className="flex-grow" />

                {/* Reset Button */}
                {!!resetValue && Math.round(sliderValue) !== resetValue && (
                    <Icons.SmallIcon
                        Icon={Icons.RestartAlt}
                        className="text-gray-100 hover:text-gray-0 cursor-pointer"
                        onClick={() => {
                            shouldNotifyRef.current = true
                            setSliderValue(resetValue)
                            if (!!createTextualValue) {
                                createTextualValue(resetValue)
                            } else {
                                defaultCreateTextualValue(resetValue)
                            }
                        }}
                    />
                )}

                {/* Textual Value of Slider */}
                <small className="text-gray-100 mono text-right">{textualValue}</small>
            </div>

            {/* Slider */}
            <input
                ref={inputRef}
                className={classNames("slider", { "slider-hover": isHoveringThumb && !isActive })}
                type="range"
                min={minSliderValue}
                max={maxSliderValue}
                value={sliderValue}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onMouseDown={() => setIsActive(true)}
                onMouseUp={() => setIsActive(false)}
                onMouseMove={handleMouseMove}
                onChange={(input) => {
                    const value = Number(input.target.value)
                    shouldNotifyRef.current = true
                    setSliderValue(Math.min(Math.max(minSliderValue, value), maxSliderValue))
                    setTextualValue(
                        !!createTextualValue
                            ? createTextualValue(value)
                            : defaultCreateTextualValue(value)
                    )
                }}
            />
        </div>
    )
}
